import { useRef, useState } from "react";
import { uploadBuilderAssistantPhoto } from "@/lib/manufacturing/builderAssistantClient";
import { uploadQcChecklistPassPhoto } from "@/lib/manufacturing/builderAssistantChecklistClient";
import { acceptQcPhoto, removeQcPhoto, retryQcPhoto, type QcPhotoKind, type QcQueuedPhoto } from "@/lib/manufacturing/qcPhotoQueue";

type Ready =
  | { ok: true; observationId: (itemId: string) => string | null }
  | { ok: false; error: string };

async function runPool(jobs: Array<() => Promise<void>>, limit: number) {
  let next = 0;
  async function worker() {
    while (next < jobs.length) {
      const index = next;
      next += 1;
      await jobs[index]();
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, jobs.length) }, () => worker()));
}

async function uploadOne(photo: QcQueuedPhoto, observationId: string | null) {
  if (photo.kind === "pass") return uploadQcChecklistPassPhoto(photo.sessionId, photo.itemId, photo.file);
  if (!observationId) return { ok: false as const, error: "This failure has no observation yet." };
  const uploaded = await uploadBuilderAssistantPhoto(observationId, photo.file);
  return uploaded.ok ? { ok: true as const } : uploaded;
}

export async function settleQcPhotos(
  photos: readonly QcQueuedPhoto[],
  observationId: (itemId: string) => string | null,
) {
  await runPool(
    photos.map((photo) => async () => {
      await uploadOne(photo, observationId(photo.itemId));
    }),
    2,
  );
}

/** Takes a photo into a local list immediately, then uploads that list in the background. */
export function useQcPhotoQueue({
  sessionId,
  ensureSaved,
  onUploaded,
}: {
  sessionId: string;
  ensureSaved: () => Promise<Ready>;
  onUploaded: (photo: QcQueuedPhoto) => void;
}) {
  const [queue, setQueue] = useState<QcQueuedPhoto[]>([]);
  const queueRef = useRef(queue);
  const claimed = useRef(new Set<string>());
  const generation = useRef(0);
  const pumping = useRef(false);
  const waiters = useRef<Array<() => void>>([]);
  const ensureRef = useRef(ensureSaved);
  const uploadedRef = useRef(onUploaded);
  ensureRef.current = ensureSaved;
  uploadedRef.current = onUploaded;

  function publish(next: QcQueuedPhoto[]) {
    queueRef.current = next;
    setQueue(next);
  }

  function notify() {
    const pending = waiters.current;
    waiters.current = [];
    for (const waiter of pending) waiter();
  }

  async function send(photo: QcQueuedPhoto, observationId: string | null, gen: number) {
    try {
      const uploaded = await uploadOne(photo, observationId);
      if (gen !== generation.current) {
        publish(queueRef.current.filter((row) => row.id !== photo.id));
        return;
      }
      if (!uploaded.ok) {
        publish(queueRef.current.map((row) => (row.id === photo.id ? { ...row, status: "failed", error: uploaded.error } : row)));
        return;
      }
      publish(queueRef.current.filter((row) => row.id !== photo.id));
      uploadedRef.current(photo);
    } catch {
      if (gen === generation.current) {
        publish(queueRef.current.map((row) => (row.id === photo.id ? { ...row, status: "failed", error: "This photo did not upload." } : row)));
      } else {
        publish(queueRef.current.filter((row) => row.id !== photo.id));
      }
    } finally {
      claimed.current.delete(photo.id);
    }
  }

  async function pump() {
    if (pumping.current) return;
    pumping.current = true;
    const gen = generation.current;
    try {
      while (gen === generation.current) {
        const batch = queueRef.current.filter((row) => row.status === "queued" && !claimed.current.has(row.id));
        if (batch.length === 0) return;
        const ready = await ensureRef.current();
        if (gen !== generation.current) return;
        if (!ready.ok) {
          publish(queueRef.current.map((row) => (batch.some((photo) => photo.id === row.id) ? { ...row, status: "failed", error: ready.error } : row)));
          return;
        }
        for (const photo of batch) claimed.current.add(photo.id);
        publish(queueRef.current.map((row) => (batch.some((photo) => photo.id === row.id) ? { ...row, status: "sending", error: null } : row)));
        await runPool(batch.map((photo) => () => send(photo, ready.observationId(photo.itemId), gen)), 2);
      }
    } finally {
      pumping.current = false;
      notify();
      if (generation.current === gen && queueRef.current.some((row) => row.status === "queued" && !claimed.current.has(row.id))) {
        void pump();
      }
    }
  }

  function take(session: string) {
    generation.current += 1;
    const leftover = queueRef.current.filter((photo) => photo.sessionId === session && !claimed.current.has(photo.id));
    const next = queueRef.current.filter((photo) => !leftover.some((row) => row.id === photo.id));
    publish(next);
    return leftover;
  }

  function add(itemId: string, kind: QcPhotoKind, file: File, savedFailCount: number) {
    const next = acceptQcPhoto(queueRef.current, {
      id: crypto.randomUUID(),
      sessionId,
      itemId,
      kind,
      file,
      savedFailCount,
    });
    if (!next.accepted) return false;
    publish(next.queue);
    void pump();
    return true;
  }

  function drain(): Promise<{ ok: true } | { ok: false; error: string }> {
    void pump();
    return new Promise((resolve) => {
      const check = () => {
        const pending = queueRef.current.some((row) => row.status === "queued" || row.status === "sending" || claimed.current.size > 0);
        if (pending || pumping.current) {
          waiters.current.push(check);
          return;
        }
        const failed = queueRef.current.find((row) => row.status === "failed");
        if (failed) resolve({ ok: false, error: failed.error || "A photo did not upload. Retry it before submitting." });
        else resolve({ ok: true });
      };
      check();
    });
  }

  return {
    queue,
    add,
    remove(id: string) {
      const next = removeQcPhoto(queueRef.current, id);
      publish(next);
    },
    retry(id: string) {
      publish(retryQcPhoto(queueRef.current, id));
      void pump();
    },
    drain,
    take,
  };
}
